import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ErrorNotice, Spinner } from '@crm/ui';
import type { Schemas } from '@crm/contracts';
import { ApiError, createApi, unwrap, type Api } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { TenantPicker } from '../pages/TenantPicker.tsx';
import { applyTheme } from './theme.ts';

const STORAGE_KEY = 'crm.tenant';

export interface TenantState {
  api: Api;
  me: Schemas['Me'];
  context: Schemas['TenantContext'];
  tenantId: string;
  switchTenant: (id: string | null) => void;
  can: (permission: string) => boolean;
}

export const TenantContext = createContext<TenantState | null>(null);

export function useTenant(): TenantState {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant hors de TenantProvider');
  return ctx;
}

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function FullPage({ children }: { children: ReactNode }) {
  return <div className="grid min-h-full place-items-center p-6">{children}</div>;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { accessToken, logout } = useAuth();
  const qc = useQueryClient();
  const [chosen, setChosen] = useState<string | null>(readStored);
  const tenantRef = useRef<string | null>(null);
  const api = useMemo(() => createApi({ token: accessToken, tenantId: () => tenantRef.current }), [accessToken]);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => unwrap(api.GET('/me')) });
  const memberships = meQuery.data?.memberships.filter((m) => m.status === 'ACTIVE') ?? [];
  const tenantId =
    memberships.find((m) => m.tenant_id === chosen)?.tenant_id ?? (memberships.length === 1 ? memberships[0]!.tenant_id : null);
  tenantRef.current = tenantId;

  const contextQuery = useQuery({
    queryKey: ['context', tenantId],
    enabled: !!tenantId,
    queryFn: () => unwrap(api.GET('/me/context', { params: { header: { 'X-Tenant-Id': tenantId! } } })),
  });

  const recordSession = useMutation({
    mutationFn: (id: string) => unwrap(api.POST('/me/sessions', { params: { header: { 'X-Tenant-Id': id } } })),
  });
  const recorded = useRef(new Set<string>());
  useEffect(() => {
    if (tenantId && contextQuery.isSuccess && !recorded.current.has(tenantId)) {
      recorded.current.add(tenantId);
      recordSession.mutate(tenantId);
    }
  }, [tenantId, contextQuery.isSuccess, recordSession]);

  useEffect(() => applyTheme(contextQuery.data?.tenant.theme), [contextQuery.data]);

  const switchTenant = useCallback(
    (id: string | null) => {
      try {
        if (id) localStorage.setItem(STORAGE_KEY, id);
        else localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* stockage indisponible : le choix vaut pour la session */
      }
      // Rien de l'entreprise précédente ne doit rester à l'écran ni en cache.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      setChosen(id);
    },
    [qc],
  );

  const permissions = useMemo(() => new Set(contextQuery.data?.permissions ?? []), [contextQuery.data]);
  const can = useCallback((p: string) => permissions.has(p), [permissions]);

  const failure = meQuery.error ?? contextQuery.error;
  if (failure) {
    const problem = failure instanceof ApiError ? failure.problem : null;
    return (
      <FullPage>
        <div className="w-full max-w-lg">
          <ErrorNotice
            title={problem?.title ?? t('errors.title')}
            detail={problem?.detail ?? String(failure)}
            requestId={problem?.request_id}
            action={
              <div className="flex gap-2">
                <Button onClick={() => void qc.invalidateQueries()}>{t('errors.retry')}</Button>
                {memberships.length > 1 ? <Button variant="secondary" onClick={() => switchTenant(null)}>{t('header.switchTenant')}</Button> : null}
                <Button variant="ghost" onClick={() => void logout()}>{t('header.logout')}</Button>
              </div>
            }
          />
        </div>
      </FullPage>
    );
  }
  if (!meQuery.data) return <FullPage><Spinner label={t('app.loading')} /></FullPage>;
  if (!tenantId) return <TenantPicker me={meQuery.data} onPick={switchTenant} onLogout={() => void logout()} />;
  if (!contextQuery.data) return <FullPage><Spinner label={t('app.loading')} /></FullPage>;

  return (
    <TenantContext.Provider value={{ api, me: meQuery.data, context: contextQuery.data, tenantId, switchTenant, can }}>
      {children}
    </TenantContext.Provider>
  );
}
